import { useState, useEffect } from "react";
import { useAuth } from "../../../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import Input from "../../../UI/Input/Input";
import Button from "../../../UI/Button/Button";
import server, { getMinistries } from "../../../../apis/server";
import ReactQuill from "react-quill-new";

import classes from "./Create.module.scss";

const ArticleCreate = ({
  articleFromList,
  clearSelectedArticle,
  articles,
  setArticles,
  setSelectedRows,
}) => {
  const { authUser } = useAuth();
  const navigate = useNavigate();

  const [publish, setPublish] = useState(
    articleFromList && articleFromList.published ? true : false,
  );

  const [publishEnabled, setPublishEnabled] = useState(
    articleFromList ? true : false,
  );

  const [saveEnabled, setSaveEnabled] = useState(null);

  const [selectedArticle, setSelectedArticle] = useState(
    articleFromList && !articleFromList.new ? articleFromList : null,
  );

  const [bodyValue, setBodyValue] = useState(
    articleFromList ? articleFromList.body : "",
  );

  const [articleForm, setArticleForm] = useState({
    title: {
      elementType: "input",
      elementConfig: {
        type: "text",
        placeholder: "Article title",
      },
      value: selectedArticle?.title || "",
      validation: { required: true },
      width: "70%",
    },
    author: {
      elementType: "input",
      elementConfig: {
        type: "text",
        placeholder: "Article author",
      },
      value: selectedArticle?.author || "",
      validation: { required: true },
      width: "70%",
    },
    datePublished: {
      elementType: "date",
      elementConfig: {
        timeInputLabel: "Time:",
        dateFormat: "MM/dd/yyyy h:mm aa",
        showTimeInput: true,
        placeholder: "Publish date",
      },
      value: selectedArticle?.datePublished
        ? new Date(selectedArticle.datePublished)
        : new Date(),
      validation: { required: true },
    },
    body: { elementType: "richtext" },
    image: {
      elementType: "image",
      elementConfig: {
        placeholder: "Featured Image",
        url: selectedArticle?.Image?.url || "",
        file: null,
      },
      value: "",
      validation: { required: false },
    },
    attachmentUrl: {
      elementType: "input",
      elementConfig: {
        type: "file",
        placeholder: "Featured File (PDF or Image)",
        accept: ".pdf,.jpg,.jpeg,.png",
      },
      value: selectedArticle?.attachmentUrl || "",
      file: null,
      validation: { required: false },
      width: "100%",
    },
    embedCode: {
      elementType: "textarea",
      elementConfig: {
        minRows: 8,
        placeholder: "Embed Code (optional)",
      },
      value: selectedArticle?.embedCode || "",
      validation: { required: false },
    },
    ministries: {
      elementType: "select",
      elementConfig: {
        options: [],
        multiple: true,
        placeholder: "Ministries",
      },
      value: selectedArticle?.Ministries
        ? selectedArticle.Ministries.map((m) => m.id.toString())
        : [],
      validation: { required: false },
    },
  });

  // EFFECT 1: Fetch options on mount
  useEffect(() => {
    getMinistries(authUser, setArticleForm);
  }, [authUser, setArticleForm]);

  // EFFECT 2: Sync form fields whenever selectedArticle updates
  useEffect(() => {
    if (selectedArticle) {
      setPublish(!!selectedArticle.published);

      if (selectedArticle.id) {
        setPublishEnabled(true);
      }

      setBodyValue(selectedArticle.body || "");

      setArticleForm((prevForm) => ({
        ...prevForm,
        title: { ...prevForm.title, value: selectedArticle.title || "" },
        author: { ...prevForm.author, value: selectedArticle.author || "" },
        datePublished: {
          ...prevForm.datePublished,
          value: selectedArticle.datePublished
            ? new Date(selectedArticle.datePublished)
            : new Date(),
        },
        embedCode: {
          ...prevForm.embedCode,
          value: selectedArticle.embedCode || "",
        },
        attachmentUrl: {
          ...prevForm.attachmentUrl,
          value: selectedArticle.attachmentUrl || "",
        },
        image: {
          ...prevForm.image,
          elementConfig: {
            ...prevForm.image.elementConfig,
            url: selectedArticle?.Image?.url || "",
          },
        },
        ministries: {
          ...prevForm.ministries,
          value: selectedArticle.Ministries
            ? selectedArticle.Ministries.map((m) => m.id.toString())
            : [],
        },
      }));
    }
  }, [selectedArticle]);

  const inputChangedHandler = (e, inputIdentifier) => {
    setSaveEnabled(true);
    const updatedArticleForm = { ...articleForm };
    const updatedFormElement = { ...updatedArticleForm[inputIdentifier] };

    if (updatedFormElement.elementType === "date") {
      updatedFormElement.value = e;
    } else if (updatedFormElement.elementType === "image") {
      updatedFormElement.elementConfig.url = URL.createObjectURL(
        e.target.files[0],
      );
      updatedFormElement.elementConfig.file = e.target.files[0];
    } else if (inputIdentifier === "attachmentUrl") {
      updatedFormElement.file = e.target.files[0];
      updatedFormElement.value = e.target.files[0].name;
    } else if (updatedFormElement.elementType === "richtext") {
      updatedFormElement.value = e.target;
    } else if (updatedFormElement.elementType === "select") {
      const selectedOptions = Array.from(e.target.selectedOptions).map(
        (option) => option.value,
      );
      updatedFormElement.value = selectedOptions;
    } else {
      updatedFormElement.value = e.target.value;
    }

    updatedFormElement.touched = true;
    updatedArticleForm[inputIdentifier] = updatedFormElement;
    setArticleForm(updatedArticleForm);
  };

  const handlePublish = async (e) => {
    e.preventDefault();

    try {
      const publishResponse = await server.put(`/article/publish`, {
        articleId: selectedArticle.id,
        published: !publish,
        orgId: authUser.orgId,
      });

      if (articles) {
        const revisedArticles = articles.map((article) => {
          return article.id === selectedArticle.id
            ? publishResponse.data
            : article;
        });

        setArticles(revisedArticles);
      }
      setPublish(!publish);
    } catch (err) {
      console.error("Publish Toggle Error:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isBodyEmpty =
      !bodyValue || bodyValue.trim() === "" || bodyValue === "<p><br></p>";

    if (isBodyEmpty) {
      alert("Please enter content in the article body before saving.");
      return;
    }

    let articleFormValues = new FormData();
    articleFormValues.append("title", articleForm.title.value);
    articleFormValues.append("author", articleForm.author.value);
    articleFormValues.append("body", bodyValue);
    articleFormValues.append("embedCode", articleForm.embedCode.value || "");
    articleFormValues.append("datePublished", articleForm.datePublished.value);
    articleFormValues.append("published", publish);
    articleFormValues.append("orgId", authUser.orgId);

    if (articleForm.image.elementConfig.file) {
      articleFormValues.append("image", articleForm.image.elementConfig.file);
    }

    // FIX: Format selected array to comma-separated string for Sequelize backend
    articleFormValues.append(
      "ministries",
      Array.isArray(articleForm.ministries.value)
        ? articleForm.ministries.value.join(",")
        : articleForm.ministries.value || "",
    );

    if (articleForm.attachmentUrl.file) {
      articleFormValues.append("attachment", articleForm.attachmentUrl.file);
    }

    try {
      let articleResponse;
      const config = {
        headers: { "content-type": "multipart/form-data" },
      };

      const hasValidId =
        selectedArticle &&
        selectedArticle.id &&
        selectedArticle.id !== "undefined";

      if (hasValidId) {
        articleFormValues.append("id", selectedArticle.id);
        articleResponse = await server.put(
          "/article",
          articleFormValues,
          config,
        );
      } else {
        articleResponse = await server.post(
          "/article",
          articleFormValues,
          config,
        );
      }

      if (
        articleResponse.status === 200 &&
        Array.isArray(articleResponse.data)
      ) {
        const updatedList = articleResponse.data;

        if (typeof setArticles === "function") {
          setArticles(updatedList);
        }

        const justSaved = updatedList.find((a) => {
          if (hasValidId) {
            return a.id === selectedArticle.id;
          }
          return a.title === articleForm.title.value;
        });

        if (justSaved) {
          setSelectedArticle(justSaved);
          setPublish(!!justSaved.published);
          setPublishEnabled(true);
          setSaveEnabled(false);
        }
      }
    } catch (err) {
      console.error("Save Error:", err);
      alert("Failed to save article.");
    }
  };

  const backClickHandler = () => {
    setSelectedRows(null);
    clearSelectedArticle(null);
  };

  const formElementsArray = [];
  for (let key in articleForm) {
    formElementsArray.push({
      id: key,
      config: articleForm[key],
    });
  }

  const form = (
    <form encType="multipart/form-data">
      {formElementsArray.map((formElement) => {
        if (formElement.config.elementType === "richtext") {
          return (
            <ReactQuill
              theme="snow"
              value={bodyValue}
              onChange={(content) => {
                setBodyValue(content);
                setSaveEnabled(true);
              }}
              key={formElement.id}
              className={classes.ArticleCreate__Quill}
            />
          );
        }

        if (formElement.id === "attachmentUrl") {
          const currentUrl =
            selectedArticle?.attachmentUrl || articleForm.attachmentUrl.value;
          const hasFile = !!currentUrl;
          const selectedFile = articleForm.attachmentUrl.file;
          const isPdf = selectedFile
            ? selectedFile.name.toLowerCase().endsWith(".pdf")
            : currentUrl?.toLowerCase().endsWith(".pdf");

          return (
            <div
              key={formElement.id}
              className={classes.ArticleCreate__InputWrapper}
            >
              {hasFile ? (
                <div className={classes.ArticleCreate__FileStatus}>
                  <div className={classes.ArticleCreate__FileSuccess}>
                    <span style={{ fontSize: "1.5rem" }}>
                      {isPdf ? "📄" : "🖼️"}
                    </span>
                    <div>
                      <p className={classes.FileName}>
                        Current Featured {isPdf ? "PDF" : "Image"} Attached
                      </p>
                      <a
                        href={currentUrl}
                        target="_blank"
                        rel="noreferrer"
                        className={classes.ViewLink}
                      >
                        View Current {isPdf ? "PDF" : "Image"} &rarr;
                      </a>
                    </div>
                  </div>
                  <p className={classes.FileInstructions}>
                    To replace, select a new file (PDF or Image):
                  </p>
                  <Input
                    elementType={formElement.config.elementType}
                    elementConfig={formElement.config.elementConfig}
                    value={formElement.config.value}
                    changed={(e) => inputChangedHandler(e, formElement.id)}
                    width={formElement.config.width}
                  />
                </div>
              ) : (
                <Input
                  elementType={formElement.config.elementType}
                  elementConfig={formElement.config.elementConfig}
                  value={formElement.config.value}
                  changed={(e) => inputChangedHandler(e, formElement.id)}
                  required={formElement.config.validation.required}
                  width={formElement.config.width}
                />
              )}
            </div>
          );
        }

        return (
          <div
            key={formElement.id}
            className={classes.ArticleCreate__InputWrapper}
          >
            <Input
              elementType={formElement.config.elementType}
              elementConfig={formElement.config.elementConfig}
              value={formElement.config.value}
              changed={(e) => inputChangedHandler(e, formElement.id)}
              required={formElement.config.validation.required}
              width={formElement.config.width}
            />
          </div>
        );
      })}

      <div className={classes.SlugPreview}>
        <small>
          <strong>Article Link:</strong> rbcommunity.org/blog/
          {selectedArticle?.slug || "pending-save"}
        </small>
      </div>
    </form>
  );

  return (
    <div className={classes.ArticleCreate}>
      {selectedArticle ? (
        <Button
          clicked={articles ? backClickHandler : () => navigate("/articles")}
        >
          &larr; Back
        </Button>
      ) : null}

      <div className={classes.ArticleCreate__TopInfo}>
        <h2>{selectedArticle ? `Edit an` : `Create a new`} article</h2>
        <div className={classes.ArticleCreate__TopInfo__Buttons}>
          <Button disabled={!publishEnabled} clicked={handlePublish}>
            {publish ? "Unpublish" : "Publish"}
          </Button>
          <Button disabled={!saveEnabled} clicked={handleSubmit}>
            Save
          </Button>
        </div>
      </div>

      {form}
    </div>
  );
};

export default ArticleCreate;
